import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Shield, Moon, Sun, X, Plus, AlertCircle } from 'lucide-react';
import {
  useDuties,
  useAssignDuty,
  useUpdateDutyAssignment,
  useRemoveDutyAssignment,
  type Duty,
} from '@/api/resources';
import { useCounterReadOnly } from '@/lib/useCounterSession';
import type { Employee } from '@/types';

/**
 * LES GARDES DE LA SEMAINE — sous le planning.
 *
 * Une garde ne rentre pas dans la grille : elle traverse la nuit et s'ajoute à
 * une journée déjà travaillée (voir `PharmacyDuty` côté serveur). Elle a donc
 * sa propre bande, juste sous le planning, là où on a les gens sous les yeux
 * pour désigner qui la prend.
 *
 * Affecter quelqu'un lui pose **les horaires de la garde**, modifiables ensuite
 * — deux personnes peuvent se relayer. Ces heures comptent dans sa balance et
 * sur sa fiche navette.
 */

const fmtHours = (h: number) =>
  Number.isInteger(h) ? `${h} h` : `${Math.floor(h)} h ${String(Math.round((h % 1) * 60)).padStart(2, '0')}`;

function DutyCard({ duty, employees }: { duty: Duty; employees: Employee[] }) {
  const readOnly = useCounterReadOnly();
  const assign = useAssignDuty();
  const updateAssignment = useUpdateDutyAssignment();
  const removeAssignment = useRemoveDutyAssignment();
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  const day = parseISO(duty.date);
  const assignedIds = new Set(duty.assignments.map((a) => a.employeeId));
  const available = employees.filter((e) => !assignedIds.has(e.id));

  async function add(employeeId: string) {
    setError(null);
    setPicking(false);
    try {
      await assign.mutateAsync({ dutyId: duty.id, employeeId });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Affectation impossible.');
    }
  }

  async function changeHours(id: string, field: 'startTime' | 'endTime', value: string) {
    if (!value) return;
    setError(null);
    try {
      await updateAssignment.mutateAsync({ id, [field]: value });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Modification impossible.');
    }
  }

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <div className="px-4 py-2.5 bg-slate-50/70 border-b border-slate-200 flex items-center gap-3">
        <Shield size={14} className="text-accent flex-shrink-0" />
        <div className="text-[12.5px] font-semibold text-slate-900 capitalize">
          {format(day, 'EEEE d MMMM', { locale: fr })}
        </div>
        <div className="flex items-center gap-1.5 text-[12px] text-slate-600 tabular-nums">
          {duty.startTime} → {duty.endTime}
          {/* Sans cette mention, « 19:30 → 08:30 » se lit comme une erreur. */}
          {duty.overnight && <span className="text-slate-400">le lendemain</span>}
          <span className="text-slate-400">·</span>
          <span>{fmtHours(duty.hours)}</span>
        </div>
        <div
          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium ${
            duty.shutter === 'OPEN'
              ? 'bg-primary-bg text-primary'
              : 'bg-slate-200 text-slate-600'
          }`}
        >
          {duty.shutter === 'OPEN' ? <Sun size={10} /> : <Moon size={10} />}
          {duty.shutter === 'OPEN' ? 'Volet ouvert' : 'Volet fermé'}
        </div>
      </div>

      <div className="p-3">
        {duty.assignments.length === 0 && !picking && (
          <div className="text-[11.5px] text-slate-500 mb-2">
            Personne n'est de garde ce jour-là.
          </div>
        )}

        <div className="flex flex-wrap gap-2 items-center">
          {duty.assignments.map((a) => (
            <div
              key={a.id}
              className="flex items-center gap-2 border border-slate-200 rounded-lg pl-2 pr-1 py-1 bg-white"
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: a.employee.color ?? '#94a3b8' }}
              />
              <span className="text-[12px] font-medium text-slate-800">
                {a.employee.firstName} {a.employee.lastName}
              </span>
              {readOnly ? (
                <span className="text-[11.5px] text-slate-600 tabular-nums">
                  {a.startTime} → {a.endTime}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <input
                    type="time"
                    value={a.startTime}
                    onChange={(e) => changeHours(a.id, 'startTime', e.target.value)}
                    className="input text-[11px] py-0.5 px-1 tabular-nums w-[72px]"
                    title="Début pour cette personne"
                  />
                  <span className="text-[10px] text-slate-400">→</span>
                  <input
                    type="time"
                    value={a.endTime}
                    onChange={(e) => changeHours(a.id, 'endTime', e.target.value)}
                    className="input text-[11px] py-0.5 px-1 tabular-nums w-[72px]"
                    title="Fin pour cette personne"
                  />
                </span>
              )}
              <span className="text-[11px] text-slate-500 tabular-nums">{fmtHours(a.hours)}</span>
              {!readOnly && (
                <button
                  onClick={() => removeAssignment.mutate(a.id)}
                  className="p-1 rounded text-slate-400 hover:text-danger hover:bg-danger-bg"
                  title="Retirer de la garde"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}

          {!readOnly && !picking && available.length > 0 && (
            <button
              onClick={() => setPicking(true)}
              className="flex items-center gap-1.5 border border-dashed border-slate-300 rounded-lg px-2.5 py-1.5 text-[12px] text-slate-600 hover:border-primary hover:text-primary"
            >
              <Plus size={12} />
              Ajouter quelqu'un
            </button>
          )}

          {picking && (
            <select
              autoFocus
              defaultValue=""
              onChange={(e) => e.target.value && add(e.target.value)}
              onBlur={() => setPicking(false)}
              className="input text-[12px] py-1 max-w-[230px]"
            >
              <option value="" disabled>
                Choisir un salarié…
              </option>
              {available.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.firstName} {e.lastName}
                </option>
              ))}
            </select>
          )}
        </div>

        {error && (
          <div className="mt-2 flex items-start gap-1.5 text-[11.5px] text-danger">
            <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DutyStrip({
  from,
  to,
  employees,
}: {
  from: string;
  to: string;
  employees: Employee[];
}) {
  const { data: duties } = useDuties(from, to);
  const readOnly = useCounterReadOnly();

  // Aucune garde cette semaine : on n'affiche rien du tout. Un bandeau vide sous
  // chaque planning finirait par ne plus être lu.
  if (!duties || duties.length === 0) return null;

  return (
    <div className="mt-4">
      <div className="flex items-baseline gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-wider font-medium text-slate-500">
          Gardes de la semaine
        </div>
        {!readOnly && (
          <div className="text-[11px] text-slate-400">
            Les heures posées ici comptent dans la balance et sur la fiche navette.
          </div>
        )}
      </div>
      <div className="space-y-2">
        {duties.map((d) => (
          <DutyCard key={d.id} duty={d} employees={employees} />
        ))}
      </div>
    </div>
  );
}
